const CACHE_NAME = 'baseboard-glb-v1'
const INDEX_KEY = 'baseboard-glb-cache-index-v1'
const MAX_ENTRIES = 4
const MAX_BYTES = 1_500_000_000

type IndexEntry = { size: number; lastUsed: number }
type CacheIndex = Record<string, IndexEntry>

export function glbCacheKey(url: string): string {
  try {
    return `/glb${new URL(url).pathname}`
  } catch {
    return `/glb/${encodeURIComponent(url)}`
  }
}

export async function readCachedGlb(cacheKey: string): Promise<ArrayBuffer | null> {
  const cache = await openCache()
  if (!cache) return null
  try {
    const hit = await cache.match(cacheKey)
    if (!hit) return null
    const buffer = await hit.arrayBuffer()
    touch(cacheKey, buffer.byteLength)
    return buffer
  } catch {
    // A partially written or evicted entry just means the caller falls through to the network.
    return null
  }
}

export async function writeCachedGlb(cacheKey: string, buffer: ArrayBuffer): Promise<void> {
  const cache = await openCache()
  if (!cache) return
  try {
    await evictFor(cache, cacheKey, buffer.byteLength)
    await cache.put(cacheKey, new Response(buffer))
    touch(cacheKey, buffer.byteLength)
  } catch {
    // Over quota, or storage is unavailable — the model still loads, it just won't be cached.
  }
}

async function openCache(): Promise<Cache | null> {
  if (typeof caches === 'undefined') return null
  try {
    return await caches.open(CACHE_NAME)
  } catch {
    return null
  }
}

async function evictFor(cache: Cache, incomingKey: string, incomingBytes: number) {
  const index = await reconcile(cache)
  const candidates = Object.entries(index)
    .filter(([key]) => key !== incomingKey)
    .sort((a, b) => a[1].lastUsed - b[1].lastUsed)

  let bytes = incomingBytes + candidates.reduce((sum, [, entry]) => sum + entry.size, 0)
  let count = candidates.length + 1

  for (const [key, entry] of candidates) {
    if (count <= MAX_ENTRIES && bytes <= MAX_BYTES) break
    await cache.delete(key)
    delete index[key]
    bytes -= entry.size
    count--
  }
  writeIndex(index)
}

/** Reunites the side index with what the Cache actually holds; either can be cleared independently. */
async function reconcile(cache: Cache): Promise<CacheIndex> {
  const index = readIndex()
  const live = new Set<string>()
  for (const request of await cache.keys()) live.add(keyOf(request))

  for (const key of Object.keys(index)) {
    if (!live.has(key)) delete index[key]
  }
  // An entry the index never saw is treated as brand new rather than immortal.
  for (const key of live) {
    if (!index[key]) index[key] = { size: 0, lastUsed: Date.now() }
  }
  return index
}

function keyOf(request: Request): string {
  try {
    return new URL(request.url).pathname
  } catch {
    return request.url
  }
}

function touch(cacheKey: string, size: number) {
  const index = readIndex()
  index[cacheKey] = { size, lastUsed: Date.now() }
  writeIndex(index)
}

function readIndex(): CacheIndex {
  try {
    const raw = localStorage.getItem(INDEX_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as CacheIndex) : {}
  } catch {
    return {}
  }
}

function writeIndex(index: CacheIndex) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index))
  } catch {
    // Without the index the cache still works, it just falls back to insertion-order eviction.
  }
}
